// src/components/ThemeBootstrapper.jsx
// On login: fetches the artist's saved THEMESET from DDB and applies it globally.
// Polls until getTenantId() returns a value (available immediately after JWT is stored).
import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { getLibraryCardRequest } from "../actions/libraryCardActions";
import { getLibraryCards } from "../getters/libraryCardGetters";
import { getTenantId } from "../utils/authUtils";

const POLL_INTERVAL_MS = 500;
const POLL_MAX_ATTEMPTS = 20; // 10 seconds max

const ThemeBootstrapper = ({
  isAuthenticated,
  onThemeLoaded,
  getLibraryCardRequest,
  cardDetails,
}) => {
  const hasFetchedRef = useRef(false);
  const hasAppliedRef = useRef(false);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      hasFetchedRef.current = false;
      hasAppliedRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || hasFetchedRef.current) return;

    const doFetch = (tenantId) => {
      hasFetchedRef.current = true;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      getLibraryCardRequest({
        operationType: "GET_LIBRARY_CARD",
        tenantId,
        entityType: "THEMESET",
        entityId: tenantId,
      });
    };

    const tenantId = getTenantId();
    if (tenantId) {
      doFetch(tenantId);
      return;
    }

    let attempts = 0;
    pollTimerRef.current = setInterval(() => {
      attempts += 1;
      const id = getTenantId();
      if (id) {
        doFetch(id);
      } else if (attempts >= POLL_MAX_ATTEMPTS) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [isAuthenticated, getLibraryCardRequest]);

  useEffect(() => {
    if (!cardDetails || !hasFetchedRef.current || hasAppliedRef.current) return;
    try {
      const parsed = JSON.parse(cardDetails);
      if (parsed?.themeSet?.length > 0) {
        hasAppliedRef.current = true;
        const selectedIdx = parsed.themeSet.findIndex((t) => t.isSelected);
        const theme = parsed.themeSet[selectedIdx >= 0 ? selectedIdx : 0];
        onThemeLoaded(theme);
        localStorage.setItem("THEME", JSON.stringify(theme));
      }
    } catch {
      // ignore parse errors
    }
  }, [cardDetails, onThemeLoaded]);

  return null;
};

const mapStateToProps = (state) => ({
  cardDetails: getLibraryCards(state),
});

const mapDispatchToProps = (dispatch) => ({
  getLibraryCardRequest: (payload) => dispatch(getLibraryCardRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThemeBootstrapper);
